import {
  MenuItemOptionProps as ChakraMenuItemOptionProps,
  forwardRef,
  useMenuOption,
} from '@chakra-ui/react';
import classnames from 'classnames';
import React from 'react';

import { OmitChakraProps } from '../../utils/typeUtils';
import { Checkbox } from '../Checkbox/Checkbox';
import { MenuItem, MenuItemProps } from '../MenuItem/MenuItem';
import { Radio } from '../Radio/Radio';

export type MenuItemOptionProps = OmitChakraProps<
  ChakraMenuItemOptionProps,
  'icon' | 'as' | 'type' | 'isChecked' | 'value' | 'onChange'
> &
  Pick<MenuItemProps, 'description' | 'rightIcon' | 'isTruncated'> & {
    as?: never;

    type: 'checkbox' | 'radio';

    isReadOnly?: boolean;

    /**
   * Warning: do not use `isChecked` if using as a child to `MenuOptionGroup` as
it will be overridden by `MenuOptionGroup` and can cause unexpected behavior.
   */
    isChecked?: boolean;

    /**
     * Warning: do not set `onChange` on the `MenuItemOption` if it is a child of
     * `MenuOptionGroup`; use the `MenuOptionGroup`'s `onChange` prop instead
     */
    onChange?: ChakraMenuItemOptionProps['onChange'];

    isIndeterminate?: boolean;

    isDisabled?: boolean;

    value: string;
  };

/**
 * A menu item that acts as a control. Can be either a `radio` or `checkbox` type.
 *
 * It is best to use this within a `MenuOptionGroup` which will manage the state of these options for you.
 */
export const MenuItemOption = forwardRef<MenuItemOptionProps, 'button'>(
  (
    {
      description,
      isReadOnly,
      isChecked,
      isIndeterminate,
      isDisabled,
      isTruncated,
      ...props
    },
    ref,
  ) => {
    const optionProps = useMenuOption(props);

    return (
      <MenuItem
        {...optionProps}
        ref={ref}
        description={description}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={classnames('orbit-menu__menuitem-option', props.className)}
        value={props.value}
        isDisabled={isDisabled}
        isTruncated={isTruncated}
        icon={
          props.type === 'radio' ? (
            <Radio
              isReadOnly={isReadOnly}
              isChecked={isChecked}
              isDisabled={isDisabled}
              __orbitStyleConfig={{
                pointerEvents: 'none',
              }}
            />
          ) : props.type === 'checkbox' ? (
            <Checkbox
              isReadOnly={isReadOnly}
              isChecked={isChecked}
              isIndeterminate={isIndeterminate}
              isDisabled={isDisabled}
              __orbitStyleConfig={{
                pointerEvents: 'none',
              }}
            />
          ) : (
            <></>
          )
        }
      >
        {optionProps.children}
      </MenuItem>
    );
  },
);

// When using MenuOptionGroup with an onChange, the onChange function was not
// working properly. Someone posted a solution in the link below by adding
// a MenuItemOption id to the wrapped component.
// https://github.com/chakra-ui/chakra-ui/issues/5011
MenuItemOption.id = 'MenuItemOption';
